import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface LoyaltyLevel {
  id: string;
  level_key: string;
  name: string;
  icon: string;
  color: string;
  threshold_lifetime_points: number;
  earn_multiplier: number;
  sort_order: number;
  benefits: string;
}

export interface LoyaltySettings {
  id: number;
  points_per_chf: number;
  points_per_chf_redemption: number;
  is_enabled: boolean;
}

export interface LoyaltyOverview {
  authenticated: boolean;
  has_account: boolean;
  balance: number;
  lifetime_earned: number;
  order_count: number;
  current_level: LoyaltyLevel | null;
  next_level: LoyaltyLevel | null;
  settings: LoyaltySettings | null;
}

/** Public settings + levels (cached, no auth) */
export function useLoyaltyConfig() {
  const [settings, setSettings] = useState<LoyaltySettings | null>(null);
  const [levels, setLevels] = useState<LoyaltyLevel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const [sRes, lRes] = await Promise.all([
        supabase.from('loyalty_settings').select('*').eq('id', 1).maybeSingle(),
        supabase.from('loyalty_levels').select('*').order('sort_order', { ascending: true }),
      ]);
      if (!active) return;
      if (sRes.data) setSettings(sRes.data as LoyaltySettings);
      if (lRes.data) setLevels(lRes.data as LoyaltyLevel[]);
      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  return { settings, levels, loading };
}

/** Compute points a customer would earn for a given CHF amount (with their current multiplier) */
export function computePointsForAmount(amount: number, settings: LoyaltySettings | null, multiplier: number = 1): number {
  if (!settings || !settings.is_enabled || amount <= 0) return 0;
  return Math.floor(amount * settings.points_per_chf * multiplier);
}

/** Convert points into CHF discount value */
export function pointsToChf(points: number, settings: LoyaltySettings | null): number {
  if (!settings || !settings.is_enabled || points <= 0) return 0;
  return Math.round((points / settings.points_per_chf_redemption) * 100) / 100;
}

/** Logged-in user's loyalty overview */
export function useMyLoyalty() {
  const { user } = useAuth();
  const [overview, setOverview] = useState<LoyaltyOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setOverview(null);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase.rpc('get_my_loyalty_overview');
    if (!error && data) {
      setOverview(data as unknown as LoyaltyOverview);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { overview, loading, refresh };
}
